import type { App } from 'vue'
import { createRouter, createWebHistory, createWebHashHistory } from 'vue-router'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { routes } from 'vue-router/auto-routes'
import { AppAuth } from './app/auth'
import { getCodepushInstance, networkStatus } from '.'
import { useUserStore } from './app/store/user'
import { App as AppInstance } from './app'
import { PUBLIC_PATHS } from './app/constant'
import { SETTING_QUERY_KEY, withoutSettingQuery } from './composables/useSettingRoute'

const BUILD_MODE = import.meta.env.VITE_BUILD_MODE
const IS_PRODUCTION = !BUILD_MODE?.includes('dev')

// 인터넷 연결이 필요한 경로 목록
const REQUIRES_NETWORK_PATHS = ['/admin/dashboard']

// 코드푸시 체크 최소 간격 (10분)
const CODEPUSH_CHECK_THROTTLE_MS = 10 * 60 * 1000
let lastCodepushCheckAt = 0

// 라우터 가드
const allRoutes = [
  ...routes,
  // 구 관리 홈 → 홈 (관리 탭 폐지)
  {
    path: '/quality/manage',
    redirect: '/quality',
  },
  // 구 과업 허브 → 과업 상세
  {
    path: '/quality/manage/tasks/:taskId',
    redirect: (to: { params: { taskId: string } }): string => `/quality/task/${to.params.taskId}`,
  },
  // 구 배정/검수/업체 페이지 → 과업 상세 해당 탭
  {
    path: '/quality/manage/tasks/:taskId/assignments',
    redirect: (to: { params: { taskId: string } }): { path: string; query: { tab: string } } => ({
      path: `/quality/task/${to.params.taskId}`,
      query: { tab: 'assign' },
    }),
  },
  {
    path: '/quality/manage/tasks/:taskId/review',
    redirect: (to: { params: { taskId: string } }): { path: string; query: { tab: string } } => ({
      path: `/quality/task/${to.params.taskId}`,
      query: { tab: 'review' },
    }),
  },
  {
    path: '/quality/manage/tasks/:taskId/partners',
    redirect: (to: { params: { taskId: string } }): { path: string; query: { tab: string } } => ({
      path: `/quality/task/${to.params.taskId}`,
      query: { tab: 'partners' },
    }),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
]
export const router = createRouter({
  // 개발 모드에서는 해시모드 사용
  history: IS_PRODUCTION ? createWebHistory(import.meta.env.BASE_URL) : createWebHashHistory(),
  routes: allRoutes,
})

/**
 * 라우트 컴포넌트 청크를 미리 받아 평가해 둔다 — 첫 탭에서 청크 로드·평가(A21s 콜드 과업 화면 ≈0.8s)를
 * 목록 화면 유휴로 옮긴다. 경로가 같은 레코드 전부(중첩 부모·index 자식), 이미 불러온 컴포넌트는 건너뛴다
 */
export function preloadRouteComponents(path: string): void {
  for (const record of router.getRoutes()) {
    if (record.path !== path) continue
    for (const component of Object.values(record.components ?? {})) {
      // vue-router와 같은 판정: 함수이면서 컴포넌트 표식이 없으면 지연 로더
      if (typeof component !== 'function' || 'displayName' in component || 'props' in component)
        continue
      ;(component as () => Promise<unknown>)().catch(() => {})
    }
  }
}

/**
 * 전역 네비게이션 가드 - 인증이 필요한 페이지 접근 제어
 */
router.beforeEach(async (to, from, next): Promise<void> => {
  // 컴포넌트 hook에서 읽을 때 타이밍 보장을 위해 beforeEach에서 갱신
  previousRoutePath = from.path

  // AppAuth 초기화 (아직 초기화되지 않은 경우에만)
  if (!AppAuth.isInitialized()) {
    await AppAuth.ensureInitialized()
  }

  // 온라인일 때만 코드푸시 체크 (non-blocking + 스로틀 - 타임아웃 없는 요청이 네비게이션을 막지 않도록)
  if (
    networkStatus.value.status &&
    Date.now() - lastCodepushCheckAt >= CODEPUSH_CHECK_THROTTLE_MS
  ) {
    lastCodepushCheckAt = Date.now()
    getCodepushInstance()
      ?.checkForUpdates()
      .catch(() => {})
  }

  const hasLoginToken = await AppAuth.hasLoginToken()

  // 로그인 상태에서 로그인 페이지 접근 시 메인으로 리다이렉트
  if (to.path === '/login' && hasLoginToken) {
    next('/')
    return
  }

  // 인증이 필요 없는 경로들
  const isPublicPath = PUBLIC_PATHS.some(path => to.path.includes(path))

  // meta.requiresAuth가 false로 명시적으로 설정된 경우도 허용
  const requiresAuth = to.meta?.requiresAuth !== false

  if (!isPublicPath && requiresAuth) {
    // 인증이 필요한 페이지인 경우 로그인 토큰 확인
    if (!hasLoginToken) {
      // 로그인 토큰이 없으면 로그인 페이지로 리다이렉트
      next('/login')
      return
    }
  }

  // 인터넷 연결이 필요한 경로 체크
  const requiresNetwork = REQUIRES_NETWORK_PATHS.some(path => to.path.startsWith(path))
  if (requiresNetwork && !networkStatus.value.status) {
    AppInstance.toastInstance.add('error', '네트워크 연결이 필요한 페이지입니다.')
    next('/')
    return
  }

  // admin 권한이 필요한 경로 체크
  if (to.path.startsWith('/admin')) {
    const userStore = useUserStore()

    // userStore는 App.vue에서 이미 초기화됨
    // 단, router guard가 먼저 실행될 수 있으므로 방어 코드 추가
    if (!userStore.userInfo) {
      try {
        await userStore.getMe()
      } catch (error) {
        console.error('Failed to load user info:', error)
        const errorMessage = networkStatus.value.status
          ? '사용자 정보를 불러올 수 없습니다.'
          : '오프라인 상태에서는 관리자 페이지에 접근할 수 없습니다.'
        AppInstance.toastInstance.add('error', errorMessage)
        next('/')
        return
      }
    }

    // 권한 체크
    if (!userStore.isAdmin) {
      AppInstance.toastInstance.add('error', '관리자 권한이 필요합니다.')
      next('/')
      return
    }
  }

  // 품질점검 회사 계정은 전용 홈으로 리다이렉트 (안전진단용 홈/과업 생성/설정 미노출)
  if (to.path === '/' && hasLoginToken) {
    const userStore = useUserStore()

    if (!userStore.userInfo) {
      try {
        // 회사 분류 판정은 캐시 우선 - 기존(비QUALITY) 사용자의 홈 진입이 네트워크 대기로 지연되지 않도록
        await userStore.getUserInfoFromLocalStorage()
        if (!userStore.userInfo) await userStore.getMe()
      } catch (error) {
        // 사용자 정보 로드 실패(오프라인 최초 실행 등) 시 기존 홈으로 통과
        console.error('Failed to load user info:', error)
      }
    }

    if (userStore.isQualityCompany) {
      next('/quality')
      return
    }
  }

  // 품질점검 전용 경로: QUALITY 회사 계정만 접근 가능
  if (to.path.startsWith('/quality') && hasLoginToken) {
    const userStore = useUserStore()

    if (!userStore.userInfo) {
      try {
        // 회사 분류 판정은 캐시 우선 (분류는 사실상 불변)
        await userStore.getUserInfoFromLocalStorage()
        if (!userStore.userInfo) await userStore.getMe()
      } catch (error) {
        // 사용자 정보 로드 실패(오프라인 재진입 등) 시 차단하지 않고 통과 (화면 권한 가드에 위임)
        console.error('Failed to load user info:', error)
      }
    }

    if (userStore.userInfo && !userStore.isQualityCompany) {
      next('/')
      return
    }

    // 총괄 권한이 필요한 관리 경로 체크
    // 관리 화면(계정 생성·삭제 등)은 자체 권한 가드가 없으므로 fail-closed:
    // userInfo 미확보(오프라인 캐시 유실 등) 시에도 통과시키지 않고 차단한다
    if (to.path.startsWith('/quality/manage') && !userStore.isQualityManager) {
      AppInstance.toastInstance.add('error', '품질점검 총괄 권한이 필요합니다.')
      next('/quality')
      return
    }
  }

  // 인증이 필요 없거나 인증된 경우 통과
  next()
})

// 직전 라우트 경로 (네비게이션 가드에서 갱신)
export let previousRoutePath = ''

const LAST_ROUTE_KEY = 'lastEditorRoute'

// 라우트 저장 제외 경로
const EXCLUDED_ROUTE_PREFIXES = ['/', '/login', '/public']

// 앱 재시작 시 초기 네비게이션('/')에서 저장된 라우트를 삭제하지 않기 위한 플래그
let isInitialNavigation = true

// 라우트 변경 시 LAST_ROUTE_KEY 저장/삭제
router.afterEach(to => {
  const isExcluded =
    to.path === '/' ||
    EXCLUDED_ROUTE_PREFIXES.some(prefix => prefix !== '/' && to.path.startsWith(prefix))

  if (!isExcluded) {
    // 설정 모달(?setting=)은 저장하지 않는다 - 재시작 복원 때 설정이 열린 채로 뜨지 않게
    const savedPath =
      SETTING_QUERY_KEY in to.query
        ? router.resolve({ path: to.path, query: withoutSettingQuery(to.query), hash: to.hash })
            .fullPath
        : to.fullPath
    localStorage.setItem(LAST_ROUTE_KEY, savedPath)
  } else if (!isInitialNavigation) {
    localStorage.removeItem(LAST_ROUTE_KEY)
  }

  isInitialNavigation = false
})

export default (vueApp: App): void => {
  try {
    vueApp.use(router)
  } catch (error) {
    console.error(error)
  }
}
